import { useEffect } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import config from 'config';

import { LAUNCHDARKLY_USER_KEYS } from 'legacy/common/constants/launchdarkly';
import { useGetMe } from 'legacy/user/hooks';

type User = {
  id: string;
  email: string;
  name: string;
  country: string;
};

// This ensures that the Cypress tests do not unnecessarily create users in LaunchDarkly and increase our MAU count.
const CYPRESS_TEST_ACCOUNT_PATTERN =
  /.+(@cypresstest.com|@unverifiedcypresstest.com)/;

export const getLaunchDarklyUser = (user: User) =>
  config.ENV !== 'production' && CYPRESS_TEST_ACCOUNT_PATTERN.test(user.email)
    ? {
        key: LAUNCHDARKLY_USER_KEYS.CYPRESS_TESTS
      }
    : {
        key: user?.id,
        email: user?.email,
        name: user?.name,
        country: user?.country
      };

const useIdentifyLaunchDarkly = (options?: { eventId?: string }) => {
  const ldClient = useLDClient();
  const { data: user } = useGetMe();

  const ldUser = user?._id
    ? getLaunchDarklyUser({
        id: user?._id,
        email: user?.email,
        name: user?.name,
        country: user?.country
      })
    : {
        key: LAUNCHDARKLY_USER_KEYS.ANONYMOUS,
        anonymous: true
      };

  /**
   * Identifies the user in LaunchDarkly.
   *
   * The useEffect right below is used to identify the user in LaunchDarkly.
   * It adds the current event ID to the launchdarkly context.
   * This is used to track any event specific flags states.
   */
  useEffect(() => {
    if (ldClient) {
      // If the event slug is present in the URL, identify the user in LaunchDarkly
      // with the event ID as the context.
      if (options?.eventId) {
        ldClient.identify({
          kind: 'multi',
          user: ldUser,
          event: {
            key: options.eventId
          }
        });
      } else {
        // If the event slug is not present in the URL, identify the user in LaunchDarkly
        // with the user ID as the context.
        ldClient.identify({
          kind: 'user',
          ...ldUser
        });
      }
    }
    return () => {
      // If the component is unmounted, remove the event ID from the launchdarkly context.
      ldClient?.identify({
        kind: 'user',
        ...ldUser
      });
    };
  }, [!!ldClient, ldUser.key, options?.eventId]);
};

export default useIdentifyLaunchDarkly;
